import { createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type DeepPartial } from "react-hook-form";
import { all } from "redux-saga/effects";

import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";
import { normalizeWeightsToSum } from "@ds/modules/content/campaigns/utils/helpers";

import { getIds } from "@ds/utils/entities";
import { LoadStatus, type BaseMeta, type PatchedModel } from "@ds/utils/reducer";
import { type SagaOptions } from "@ds/utils/saga-helpers";

import { apiToModelNormalize, normalize } from "../utils/normalizer";
import { watchCampaignItems } from "./sagas";

export enum CampaignItemsRemoteOperation {
  CRUD = "crud",
  CHECKBOXES = "checkboxes",
  IOT_FETCH = "iot_fetch",
}

export type CampaignItemsMeta = BaseMeta & {
  remoteOperation: CampaignItemsRemoteOperation;
  filters?: KeysToCamelCase<ContentCampaignItemFilters>;
};

interface CampaignItemsState {
  loadStatusMap: {
    [CampaignItemsRemoteOperation.CRUD]: LoadStatus;
    [CampaignItemsRemoteOperation.CHECKBOXES]: LoadStatus;
    [CampaignItemsRemoteOperation.IOT_FETCH]: LoadStatus;
  };

  error: string | null;

  entities: {
    [campaignItemId: number]: CampaignItem;
  };

  ids: number[];
  tableIds: Record<string, number[]>;
  selectedIds: Record<string, number[]>;
}

const entityAdapter = createEntityAdapter<CampaignItem>();

export const initialState: CampaignItemsState = entityAdapter.getInitialState({
  loadStatusMap: {
    [CampaignItemsRemoteOperation.CRUD]: LoadStatus.Idle,
    [CampaignItemsRemoteOperation.CHECKBOXES]: LoadStatus.Idle,
    [CampaignItemsRemoteOperation.IOT_FETCH]: LoadStatus.Idle,
  },

  error: null,

  tableIds: { [DetailsTableDataTypeEnum.ContentCampaignItems]: [] },
  selectedIds: { [DetailsTableDataTypeEnum.ContentCampaignItems]: [] },
}) as CampaignItemsState;

const getTableTypeOrDefault = (
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = DetailsTableDataTypeEnum.ContentCampaignItems,
) => tableType;

const removeEntities = (state: CampaignItemsState, payload: number[]) => {
  entityAdapter.removeMany(state, payload);

  state.tableIds[DetailsTableDataTypeEnum.ContentCampaignItems] = state.tableIds[
    DetailsTableDataTypeEnum.ContentCampaignItems
  ].filter(id => !payload.includes(id));

  state.selectedIds[DetailsTableDataTypeEnum.ContentCampaignItems] = state.selectedIds[
    DetailsTableDataTypeEnum.ContentCampaignItems
  ].filter(id => !payload.includes(id));
};

const sliceType = "CONTENT_CAMPAIGN_ITEMS";
const slice = createSlice({
  name: sliceType,
  initialState,
  reducers: {
    fetchCampaignItems: {
      reducer(state, { meta }: PayloadAction<undefined, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;

        if (meta.remoteOperation === CampaignItemsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] =
            state.tableIds[getTableTypeOrDefault(meta.options?.tableType)];
        }
      },
      prepare(
        filters: KeysToCamelCase<ContentCampaignItemFilters>,
        options: SagaOptions = {},
        remoteOperation: CampaignItemsRemoteOperation = CampaignItemsRemoteOperation.CRUD,
      ) {
        return { payload: undefined, meta: { remoteOperation, filters, options } };
      },
    },
    fetchCampaignItemsFailed: {
      reducer(state, { meta, error }: PayloadAction<undefined, string, CampaignItemsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;

        if (meta.remoteOperation === CampaignItemsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = [];
        }
      },
      prepare(meta: CampaignItemsMeta, { message }: ErrorLike) {
        return { payload: undefined, meta, error: message };
      },
    },
    fetchCampaignItemsSucceeded: {
      reducer(
        state,
        {
          payload,
          meta,
        }: PayloadAction<QueryOutput<CampaignItem> | QueryOutput<ApiCampaignItem>, string, CampaignItemsMeta>,
      ) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setMany(state, normalizeWeightsToSum(apiToModelNormalize(payload.items)));

        if (meta.remoteOperation === CampaignItemsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
        } else if (meta.options?.tableType) {
          state.tableIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
            getTableTypeOrDefault(meta.options?.tableType)
          ].filter(id => state.ids.includes(id));
        }
      },
      prepare(payload: QueryOutput<CampaignItem> | QueryOutput<ApiCampaignItem>, meta: CampaignItemsMeta) {
        return { payload, meta };
      },
    },
    createCampaignItem: {
      reducer(state, { meta }: PayloadAction<CampaignItem, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: CampaignItem, filters: KeysToCamelCase<ContentCampaignItemFilters>) {
        return {
          payload,
          meta: { remoteOperation: CampaignItemsRemoteOperation.CRUD, filters, options: {} },
        };
      },
    },
    createCampaignItemFailed: {
      reducer(state, { meta, error }: PayloadAction<CampaignItem, string, CampaignItemsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: CampaignItem, meta: CampaignItemsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    createCampaignItemSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<ApiCampaignItem, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, apiToModelNormalize(payload));
      },
      prepare(payload: ApiCampaignItem, meta: CampaignItemsMeta) {
        return { payload, meta };
      },
    },
    updateCampaignItems: {
      reducer(state, _action: PayloadAction<PatchedModel<CampaignItem>>) {
        state.loadStatusMap[CampaignItemsRemoteOperation.CRUD] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(changedIds: number[], changedFields: DeepPartial<CampaignItem>) {
        return { payload: { ids: changedIds, data: changedFields } };
      },
    },
    updateCampaignItemsFailed: {
      reducer(state, { error }: PayloadAction<PatchedModel<CampaignItem>, string, never, string>) {
        state.loadStatusMap[CampaignItemsRemoteOperation.CRUD] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: PatchedModel<CampaignItem>, { message }: ErrorLike) {
        return { payload, error: message };
      },
    },
    updateCampaignItemsSucceeded: (state, { payload }: PayloadAction<ApiCampaignItem[]>) => {
      state.loadStatusMap[CampaignItemsRemoteOperation.CRUD] = LoadStatus.Succeeded;
      state.error = null;

      const normalized = apiToModelNormalize(payload);
      entityAdapter.setMany(
        state,
        normalized.map(item => ({
          ...item,
          playlist_items_count: item.playlist_items_count ?? state.entities[item.id]?.playlist_items_count ?? 0,
        })),
      );
    },
    deleteCampaignItems: {
      reducer(state, { meta }: PayloadAction<number[], string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(idsToDelete: number[], filters: KeysToCamelCase<ContentCampaignItemFilters>, redirectTo?: string) {
        return {
          payload: idsToDelete,
          meta: { remoteOperation: CampaignItemsRemoteOperation.CRUD, filters, options: { redirectTo } },
        };
      },
    },
    deleteCampaignItemsFailed: {
      reducer(state, { meta, error }: PayloadAction<number[], string, CampaignItemsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(idsToDelete: number[], meta: CampaignItemsMeta, { message }: ErrorLike) {
        return { payload: idsToDelete, meta, error: message };
      },
    },
    deleteCampaignItemsSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        removeEntities(state, payload);
      },
      prepare(idsToDelete: number[], meta: CampaignItemsMeta) {
        return { payload: idsToDelete, meta };
      },
    },
    reorderCampaignItems: {
      reducer(state, { meta }: PayloadAction<{ currIndex: number; newIndex: number }, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: { currIndex: number; newIndex: number }, filters: KeysToCamelCase<ContentCampaignItemFilters>) {
        return { payload, meta: { remoteOperation: CampaignItemsRemoteOperation.CRUD, filters } };
      },
    },
    reorderCampaignItemsFailed: {
      reducer(
        state,
        { meta, error }: PayloadAction<{ currIndex: number; newIndex: number }, string, CampaignItemsMeta, string>,
      ) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: { currIndex: number; newIndex: number }, meta: CampaignItemsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    reorderCampaignItemsSucceeded: {
      reducer(state, { meta }: PayloadAction<{ currIndex: number; newIndex: number }, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;
      },
      prepare(payload: { currIndex: number; newIndex: number }, meta: CampaignItemsMeta) {
        return { payload, meta };
      },
    },
    selectCampaignItems: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload;
      },
      prepare(campaignItemsOrIds: CampaignItem[] | ApiCampaignItem[] | number[], options: SagaOptions = {}) {
        return {
          payload: getIds(campaignItemsOrIds),
          meta: { remoteOperation: CampaignItemsRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    toggleSelected: {
      reducer(state, { payload, meta }: PayloadAction<number, string, CampaignItemsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].includes(payload)
          ? state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)].filter(id => id !== payload)
          : [...state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)], payload];
      },
      prepare(campaignItemId: number, options: SagaOptions = {}) {
        return {
          payload: campaignItemId,
          meta: { remoteOperation: CampaignItemsRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    batchUpdateCampaignItemWeights: {
      reducer(state, _action: PayloadAction<Array<{ id: number; fields: DeepPartial<CampaignItem> }>>) {
        state.loadStatusMap[CampaignItemsRemoteOperation.CRUD] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(updates: Array<{ id: number; fields: DeepPartial<CampaignItem> }>) {
        return { payload: updates };
      },
    },
    iotUpdate: (state, { payload }: PayloadAction<ApiCampaignItem>) => {
      if (payload.audit?.deleted_at) {
        removeEntities(state, [payload.id]);
      } else {
        entityAdapter.setOne(state, apiToModelNormalize(normalize(payload)));
      }
    },
  },
});

export const { name: campaignItemsType, actions: campaignItemsActions, reducer: campaignItemsReducer } = slice;

export const entitySelectors = entityAdapter.getSelectors();

export function* rootCampaignItemsSaga() {
  yield all([watchCampaignItems()]);
}
